import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notify } from '@/lib/notify';
import configurationService from '@/services/configurationService';
import type {
  IndexingSettingsResponse,
  LLMEmbeddingModelCatalogEntry,
  LLMProvider,
} from '@/services/configurationService';

type IndexingSettingsCardProps = {
  organizationId: string | null | undefined;
};

/** Provider type used when an org has declared no providers of its own. */
const FALLBACK_EMBEDDING_PROVIDER = 'openai';

/**
 * Keep only embedding models the organization can actually reach.
 *
 * An embedding model is unusable unless its provider is configured for the org
 * — offering `mistral-embed` with no Mistral provider would just fail at
 * indexing time with "no API key". Two deliberate exceptions:
 *   - with no providers at all, fall back to OpenAI so the org is not locked out
 *     (that is what runtime resolution does via the server key);
 *   - the currently selected model is always kept, so an existing choice never
 *     silently disappears from the dropdown.
 */
function filterEmbeddingModels(
  models: LLMEmbeddingModelCatalogEntry[],
  providers: LLMProvider[],
  selectedModel: string
): LLMEmbeddingModelCatalogEntry[] {
  const activeTypes = new Set(
    providers.filter((provider) => provider.is_active).map((p) => p.provider_type)
  );
  const allowed = activeTypes.size > 0 ? activeTypes : new Set([FALLBACK_EMBEDDING_PROVIDER]);

  return models.filter(
    (model) => allowed.has(model.provider ?? FALLBACK_EMBEDDING_PROVIDER) || model.id === selectedModel
  );
}

/**
 * Org-level indexing settings shown in the "Service IA" tab.
 * Controls ontology (entity) extraction toward the knowledge graph with
 * three-state semantics: inherit env default / enabled / disabled, plus the
 * OpenAI embedding model used to vectorise documents during indexing.
 */
export function IndexingSettingsCard({ organizationId }: IndexingSettingsCardProps) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<IndexingSettingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [embeddingModels, setEmbeddingModels] = useState<LLMEmbeddingModelCatalogEntry[]>([]);
  const [embeddingModel, setEmbeddingModel] = useState<string>('');
  const [savingEmbedding, setSavingEmbedding] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;

    setLoading(true);
    Promise.all([
      configurationService.getIndexingSettings(organizationId),
      configurationService.getLLMRuntimeSettings(organizationId),
      configurationService.getLLMModelCatalog(),
      configurationService.getLLMProviders(organizationId),
    ])
      .then(([indexingData, runtimeData, catalogData, providerData]) => {
        if (cancelled) return;
        setSettings(indexingData);

        const selected = runtimeData.features?.embedding?.model ?? '';
        setEmbeddingModel(selected);
        setEmbeddingModels(
          filterEmbeddingModels(
            catalogData.embedding_models ?? [],
            providerData.providers ?? [],
            selected
          )
        );
      })
      .catch((error) => {
        console.error('Failed to load indexing settings:', error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const handleEmbeddingModelChange = useCallback(
    async (model: string) => {
      if (!organizationId || !model) return;

      const previous = embeddingModel;
      setEmbeddingModel(model);
      setSavingEmbedding(true);
      try {
        const updated = await configurationService.updateLLMRuntimeSettings(organizationId, {
          features: { embedding: { provider: 'openai', model } },
        });
        setEmbeddingModel(updated.features?.embedding?.model ?? model);
        notify.success(
          t('knowledgeManagement.indexingSettings.embeddingSaveSuccess', "Modèle d'embedding mis à jour")
        );
      } catch (error) {
        console.error('Failed to update embedding model:', error);
        setEmbeddingModel(previous);
        notify.error(
          t(
            'knowledgeManagement.indexingSettings.embeddingSaveError',
            "Erreur lors de la mise à jour du modèle d'embedding"
          )
        );
      } finally {
        setSavingEmbedding(false);
      }
    },
    [organizationId, embeddingModel, t]
  );

  const handleChange = useCallback(
    async (value: boolean | null) => {
      if (!organizationId) return;

      setSaving(true);
      try {
        const updated = await configurationService.updateIndexingSettings(organizationId, {
          ontology_extraction_enabled: value,
        });
        setSettings(updated);
        notify.success(
          t('knowledgeManagement.indexingSettings.saveSuccess', "Réglage d'extraction d'entités mis à jour")
        );
      } catch (error) {
        console.error('Failed to update indexing settings:', error);
        notify.error(
          t(
            'knowledgeManagement.indexingSettings.saveError',
            "Erreur lors de la mise à jour du réglage d'indexation"
          )
        );
      } finally {
        setSaving(false);
      }
    },
    [organizationId, t]
  );

  const handleHopLimitChange = useCallback(
    async (raw: string) => {
      if (!organizationId) return;
      const value = raw === 'default' ? null : Number(raw);
      setSaving(true);
      try {
        const updated = await configurationService.updateIndexingSettings(organizationId, {
          rag_graph_hop_limit: value,
        });
        setSettings(updated);
        notify.success(
          t('knowledgeManagement.indexingSettings.hopLimitSaveSuccess', 'Graph hop limit updated')
        );
      } catch (error) {
        console.error('Failed to update hop limit:', error);
        notify.error(
          t('knowledgeManagement.indexingSettings.hopLimitSaveError', 'Failed to update the hop limit')
        );
      } finally {
        setSaving(false);
      }
    },
    [organizationId, t]
  );

  const enabledLabel = t('knowledgeManagement.indexingSettings.enabled', 'Activée');
  const disabledLabel = t('knowledgeManagement.indexingSettings.disabled', 'Désactivée');

  const selectValue =
    settings?.ontology_extraction_enabled === null || settings?.ontology_extraction_enabled === undefined
      ? 'inherit'
      : settings.ontology_extraction_enabled
        ? 'enabled'
        : 'disabled';

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div>
        <h3 className="text-base font-semibold">
          {t('knowledgeManagement.indexingSettings.title', 'Indexation des documents')}
        </h3>
        <p className="text-xs text-muted-foreground">
          {t(
            'knowledgeManagement.indexingSettings.description',
            "Contrôlez l'extraction d'entités vers le graphe de connaissances lors de l'indexation des documents."
          )}
        </p>
      </div>

      {loading || !settings ? (
        <p className="text-sm text-muted-foreground">
          {t('knowledgeManagement.indexingSettings.loading', "Chargement des réglages d'indexation...")}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="ontology-extraction-setting" className="text-sm font-medium">
            {t(
              'knowledgeManagement.indexingSettings.ontologyExtractionLabel',
              "Extraction d'entités (graphe de connaissances)"
            )}
          </label>
          <select
            id="ontology-extraction-setting"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={selectValue}
            disabled={saving}
            onChange={(event) => {
              const { value } = event.target;
              void handleChange(value === 'inherit' ? null : value === 'enabled');
            }}
          >
            <option value="inherit">
              {t('knowledgeManagement.indexingSettings.inheritOption', {
                defaultValue: 'Hériter du défaut ({{state}})',
                state: settings.env_default ? enabledLabel : disabledLabel,
              })}
            </option>
            <option value="enabled">{enabledLabel}</option>
            <option value="disabled">{disabledLabel}</option>
          </select>
          <span className="text-xs text-muted-foreground">
            {t('knowledgeManagement.indexingSettings.effectiveState', {
              defaultValue: 'État effectif : {{state}}',
              state: settings.effective ? enabledLabel : disabledLabel,
            })}
          </span>
        </div>
      )}

      {!loading && settings && (
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <label htmlFor="rag-graph-hop-limit" className="text-sm font-medium">
            {t(
              'knowledgeManagement.indexingSettings.hopLimitLabel',
              'Knowledge graph hop limit (RAG)'
            )}
          </label>
          <select
            id="rag-graph-hop-limit"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={
              settings.rag_graph_hop_limit === null || settings.rag_graph_hop_limit === undefined
                ? 'default'
                : String(settings.rag_graph_hop_limit)
            }
            disabled={saving}
            onChange={(event) => void handleHopLimitChange(event.target.value)}
          >
            <option value="default">
              {t('knowledgeManagement.indexingSettings.hopLimitDefault', {
                defaultValue: 'Default ({{hops}})',
                hops: settings.rag_graph_hop_limit_default ?? 2,
              })}
            </option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
          <span className="text-xs text-muted-foreground">
            {t('knowledgeManagement.indexingSettings.hopLimitEffective', {
              defaultValue: 'Effective: {{hops}} hop(s)',
              hops: settings.effective_rag_graph_hop_limit ?? 2,
            })}
          </span>
          <p className="w-full text-xs text-muted-foreground">
            {t(
              'knowledgeManagement.indexingSettings.hopLimitHint',
              'How far graph fusion may walk from a seed chunk during RAG (1–3). Requires RAG fusion enabled on the server.'
            )}
          </p>
        </div>
      )}

      {!loading && settings && (
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <label htmlFor="embedding-model-setting" className="text-sm font-medium">
            {t('knowledgeManagement.indexingSettings.embeddingModelLabel', "Modèle d'embedding")}
          </label>
          <select
            id="embedding-model-setting"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={embeddingModel}
            disabled={savingEmbedding || embeddingModels.length === 0}
            onChange={(event) => void handleEmbeddingModelChange(event.target.value)}
          >
            {!embeddingModel && <option value="">--</option>}
            {embeddingModels.map((model) => (
              <option key={model.id} value={model.id}>
                {model.label}
              </option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">
            {t(
              'knowledgeManagement.indexingSettings.embeddingModelHint',
              'Utilisé pour vectoriser les documents lors de l’indexation.'
            )}
          </span>

          {settings.requires_reindex && settings.dimension_conflict ? (
            <p className="w-full text-xs text-amber-600">
              {t('knowledgeManagement.indexingSettings.reindexRequired', {
                defaultValue:
                  'Ce modèle produit des vecteurs de {{modelDimensions}} dimensions alors que l’index en attend {{indexDimensions}}. L’indexation est bloquée tant que les documents n’ont pas été réindexés.',
                modelDimensions: settings.dimension_conflict.model_dimensions,
                indexDimensions: settings.dimension_conflict.index_dimensions,
              })}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default IndexingSettingsCard;
